import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { HERO_POSTER, HERO_VIDEO, WA_URL, BRAND } from "./data";

const line = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
};
const word = {
  hidden: { y: "110%", opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 1.05, ease: [0.2, 0.9, 0.2, 1] },
  },
};

function MaskLine({ tokens, delay = 0 }) {
  return (
    <span className="reveal-line">
      <motion.span
        variants={line}
        initial="hidden"
        animate="show"
        transition={{ delayChildren: delay }}
        className="block"
      >
        {tokens.map((t, i) => (
          <motion.span key={i} variants={word} className="inline-block mr-[0.28em]">
            {t}
          </motion.span>
        ))}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.04]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      id="top"
      ref={wrapRef}
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[color:var(--ink)] text-[color:var(--paper)]"
    >
      {/* -------- MOBILE (up to md): stacked layout ---------- */}
      <div className="md:hidden">
        {/* Video block — sits right under nav, natural aspect, no side crop */}
        <div className="relative w-full bg-[color:var(--ink)] pt-[72px]">
          <video
            data-testid="hero-video-mobile"
            className="block w-full h-auto object-contain bg-[color:var(--ink)]"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={HERO_POSTER}
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
        </div>

        {/* Text block below video */}
        <div className="px-5 pt-6 pb-10 bg-[color:var(--ink)]">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.9 }}
            className="flex items-center gap-3 mb-4"
          >
            <span className="h-px w-6 bg-[color:var(--paper)]/70" />
            <span className="font-mono-alt text-[color:var(--paper)]/80">
              {BRAND.location}
            </span>
          </motion.div>

          <h2
            data-testid="hero-title-mobile"
            className="font-display leading-[1] tracking-[-0.02em] text-[2.2rem]"
          >
            <MaskLine tokens={["Wake", "with", "the", "mist,"]} delay={0.1} />
            <MaskLine
              tokens={[
                <span key="i" className="font-display-italic text-[color:var(--sand)]">rest</span>,
                "in",
                "the",
                "estate.",
              ]}
              delay={0.4}
            />
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.85 }}
            className="mt-5 text-[0.94rem] leading-relaxed text-[color:var(--paper)]/85"
          >
            Twenty-five acres of coffee estate, folded into the Western Ghats.
            A homestay for slow travellers, campfire sitters and the ones who
            came for the fog.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.85 }}
            className="mt-6 flex flex-col gap-3"
          >
            <a
              data-testid="hero-book-cta-mobile"
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full justify-center"
            >
              Book on WhatsApp <ArrowUpRight size={16} />
            </a>
            <a
              href="#about"
              data-testid="hero-explore-cta-mobile"
              className="btn-ghost w-full justify-center"
            >
              <Play size={14} /> Explore
            </a>
          </motion.div>
        </div>
      </div>

      {/* -------- DESKTOP (md and up): cinematic overlay hero ---------- */}
      <div className="hidden md:block relative h-[100svh] min-h-[640px]">
        <motion.div
          style={{ y, scale }}
          className="absolute inset-0 will-change-transform"
        >
          <video
            data-testid="hero-video"
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={HERO_POSTER}
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
        </motion.div>

        <div className="absolute inset-0 bg-[color:var(--ink)]/40" />
        <div
          className="absolute inset-x-0 top-0 h-36 bg-[color:var(--ink)]/70 pointer-events-none"
          style={{
            maskImage: "linear-gradient(to bottom, black 60%, transparent)",
            WebkitMaskImage: "linear-gradient(to bottom, black 60%, transparent)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-56 bg-[color:var(--ink)]/70 pointer-events-none"
          style={{
            maskImage: "linear-gradient(to top, black 60%, transparent)",
            WebkitMaskImage: "linear-gradient(to top, black 60%, transparent)",
          }}
        />
        <div className="grain absolute inset-0" />

        <motion.div
          style={{ opacity }}
          className="relative z-10 h-full mx-auto max-w-[1440px] px-8 lg:px-12 flex flex-col justify-end pt-32 pb-20 lg:pb-24"
        >
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.9 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-8 bg-[color:var(--paper)]/70" />
            <span className="font-mono-alt text-[color:var(--paper)]/85">
              {BRAND.location}
            </span>
          </motion.div>

          <h2
            data-testid="hero-title"
            className="font-display leading-[1] tracking-[-0.02em] text-[clamp(2.6rem,5.2vw,4.6rem)]"
          >
            <MaskLine tokens={["Wake", "with", "the", "mist,"]} delay={0.1} />
            <MaskLine
              tokens={[
                <span key="i" className="font-display-italic text-[color:var(--sand)]">rest</span>,
                "in",
                "the",
                "estate.",
              ]}
              delay={0.4}
            />
          </h2>

          <div className="mt-8 flex items-end justify-between gap-6">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.85 }}
              className="max-w-md text-[0.98rem] leading-relaxed text-[color:var(--paper)]/85"
            >
              Twenty-five acres of coffee estate, folded into the Western Ghats.
              A homestay for slow travellers, campfire sitters and the ones who
              came for the fog.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.85 }}
              className="flex flex-wrap items-center gap-3"
            >
              <a
                data-testid="hero-book-cta"
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Book on WhatsApp <ArrowUpRight size={16} />
              </a>
              <a href="#about" data-testid="hero-explore-cta" className="btn-ghost">
                <Play size={14} /> Explore
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
